import { defaultFieldsToRemove } from "@contentful-api/constants/queryFields";
import structuredClone from "@ungap/structured-clone";

type Data = Record<string, any> | any[];

/**
 * Recursively removes specified properties from an object/array in place,
 * while avoiding circular references.
 *
 * @param data - The object or array to be modified.
 * @param filters - The properties to be removed.
 * @param seen - A WeakSet to track visited objects and avoid circular references.
 * @param pageReferencesCount - Tracks the number of "PagePath" references.
 */
export const deepCleanInPlace = (
    data: Data,
    filters: Set<string>,
    seen: WeakSet<object> = new WeakSet(),
    pageReferencesCount: number = 0
): void => {
    if (!data || typeof data !== "object" || seen.has(data)) return;

    seen.add(data);

    for (const key of Object.keys(data)) {
        const value = data[key];

        if (filters.has(key)) {
            delete data[key];
            continue;
        }

        if (value && typeof value === "object") {
            if (
                value?.sys?.contentType?.sys?.type === "Link" &&
                value?.sys?.contentType?.sys?.id === "pagePath"
            ) {
                pageReferencesCount++;
            }

            deepCleanInPlace(value, filters, seen, pageReferencesCount);

            // If multiple "PagePath" references exist, ensure "Page" gets removed
            if (pageReferencesCount > 1) {
                filters.add("page");
            }
        }
    }

    seen.delete(data);
};

export const deepClean = <T extends Data>(data: T): T => {
    if (data == null) return data;

    const clonedData = structuredClone(data);
    deepCleanInPlace(clonedData, new Set(defaultFieldsToRemove));

    return clonedData;
};
